/**
 * 布局环境检测的唯一来源（PROMPT 要求：断点判断集中在一处，不散落各组件）。
 *
 * - isMobile：视口 < 768px → H5 单栏布局；≥ 768px → PC 双栏布局
 * - isStandalone：iOS Web Clip / 已安装 PWA 全屏运行（无浏览器返回按钮，
 *   需要应用内返回 + 右滑手势 + SW 更新提示，见 docs/ 中 iOS Web Clip 要求）
 *
 * 本 store 已作为数据源挂进全局 store（src/store/global.ts 的 layout 字段），
 * 组件一律通过 `useLayoutEnv()` / `useGlobal((s) => s.layout)` 读取，
 * 不要另起一套 matchMedia。
 */

/** 与 src/styles/_breakpoints.scss 的 $desktop-min 保持一致，改一处必须同步另一处。 */
export const DESKTOP_MIN_WIDTH = 768;

export interface LayoutEnv {
  readonly isMobile: boolean;
  readonly isStandalone: boolean;
}

/** 测试可注入的最小 matchMedia 形状（MQL 的 EventTarget 化在 Chrome 45+/Safari 14+，覆盖支持下限）。 */
export type MinimalMediaQueryList = Pick<
  MediaQueryList,
  'matches' | 'addEventListener' | 'removeEventListener'
>;
export type MatchMediaFn = (query: string) => MinimalMediaQueryList;

export interface LayoutEnvStore {
  get(): LayoutEnv;
  /** 返回取消订阅函数。通知后用 get() 取新快照；快照无变化时不通知且对象引用不变。 */
  subscribe(listener: () => void): () => void;
}

const DESKTOP_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;
const STANDALONE_QUERY = '(display-mode: standalone)';

export function createLayoutEnvStore(
  matchMediaFn: MatchMediaFn,
  /** iOS Safari 专有 navigator.standalone，每次启动固定不变，构造时传入。 */
  iosStandalone: boolean,
): LayoutEnvStore {
  const desktopMql = matchMediaFn(DESKTOP_QUERY);
  const standaloneMql = matchMediaFn(STANDALONE_QUERY);
  const listeners = new Set<() => void>();

  let snapshot: LayoutEnv = compute();

  function compute(): LayoutEnv {
    return {
      isMobile: !desktopMql.matches,
      isStandalone: standaloneMql.matches || iosStandalone,
    };
  }

  function onChange(): void {
    const next = compute();
    if (next.isMobile === snapshot.isMobile && next.isStandalone === snapshot.isStandalone) {
      return;
    }
    snapshot = next;
    for (const listener of listeners) {
      listener();
    }
  }

  // 模块级单例、与页面同生命周期，不需要销毁逻辑
  desktopMql.addEventListener('change', onChange);
  standaloneMql.addEventListener('change', onChange);

  return {
    get: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

let defaultStore: LayoutEnvStore | undefined;

/** 浏览器环境下的默认单例（懒初始化，避免在 node 测试环境 import 即触碰 window）。 */
export function layoutEnvStore(): LayoutEnvStore {
  if (!defaultStore) {
    // navigator.standalone 是 iOS Safari 私有属性，标准类型里没有
    const nav = navigator as Navigator & { standalone?: boolean };
    defaultStore = createLayoutEnvStore(
      (query) => window.matchMedia(query),
      nav.standalone === true,
    );
  }
  return defaultStore;
}
